import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core";
import { Table, TableBody, TableCell, TableRow, Checkbox, Typography, Button, Box, Card, Grid, List, ListItem, ListItemIcon, ListItemText, IconButton, Backdrop, CircularProgress } from "@mui/material";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import { useTheme } from "@mui/material/styles";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { parsePrice } from "src/utils/parse-price";
import { api } from "src/api/endpoints";
import { AddressElement, CardElement, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import toast from "react-hot-toast";
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import FlagIcon from '@mui/icons-material/Flag';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PlaceIcon from '@mui/icons-material/Place';
import MarkunreadMailboxIcon from '@mui/icons-material/MarkunreadMailbox';
import EditIcon from '@mui/icons-material/Edit';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import AddIcon from '@mui/icons-material/Add';
import PaymentIcon from '@mui/icons-material/Payment';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch } from "react-redux";
import { updateCustomerDetails, updateCustomerPM } from "src/redux/actions/Subscription";

const BillingAccount = ({ user, customerDetails, customerPaymentMethods }) => {
    const { t } = useTranslation();
    const elements = useElements()
    const stripe = useStripe()
    const dispatch = useDispatch()

    // const [customerPaymentMethods, setCustomerPaymentMethods] = useState(null)
    // const [customerDetails, setCustomerDetails] = useState(null)
    const [createCustomerActive, setCreateCustomerActive] = useState(false)
    const [customerEmail, setCustomerEmail] = useState(user.email)
    const [customerDetailsEditActive, setCustomerDetailsEditActive] = useState(false)
    const [addPaymentMethodActive, setAddPaymentMethodActive] = useState(false)
    const [loadingBackdrop, setLoadingBackdrop] = useState(false)

    // useEffect(() => {
    //     const getDataOnStartup = async () => {
    //         if(user.stripeCustomerId){
    //             const customerDetailsRes = await api.post('/payment/getCustomerDetails', {customerId: user.stripeCustomerId})
    //             const customerPaymentMethodsRes = await api.post('/payment/listUsersPaymentMethods', {customerId: user.stripeCustomerId})

    //             setCustomerPaymentMethods(customerPaymentMethodsRes.data.methods)
    //             setCustomerDetails(customerDetailsRes.data.customer)
    //         }
    //     }
    //     if(!customerDetails) {
    //         getDataOnStartup()
    //     }
    // }, [])

    const handleNewCustomerAdd = async () => {
        setLoadingBackdrop(true)
        if(customerEmail){
            const addressElement = elements.getElement('address')
            const {complete, value} = await addressElement.getValue()
            if(value.phone) {
                  if(complete){
                      try {
                          const {data, status} = await api.post('/payment/createCustomer', {
                              userId: user._id,
                              email: customerEmail,
                              name: value.name,
                              city: value.address.city,
                              country: value.address.country,
                              line1: value.address.line1,
                              line2: value.address.line2 ? value.address.line2 : '',
                              postalCode: value.address.postal_code,
                              state: value.address.state,
                              phone: value.phone
                          }) 
                        setTimeout(() => {
                            window.location.reload()
                        }, 500)
                      } catch (error) {
                          setLoadingBackdrop(false)
                          toast.error(t("views.subscriptions.addBillingAccountError"))
                      }
                  }
            }else{
                toast.error(t("views.subscriptions.phoneRequired"))      
            }
        }else{
          toast.error(t("views.subscriptions.emailRequired"))
        }
    }

    const handleCustomerDataEdit = async () => {
        setLoadingBackdrop(true)
        if(customerEmail){
            const addressElement = elements.getElement("address")
            const {complete, value} = await addressElement.getValue()
            if(value.phone) {
                  if(complete){
                      try {
                        setLoadingBackdrop(false)
                          const {data, status} = await api.post('/payment/editCustomer', {
                              customerId: user.stripeCustomerId,
                              email: customerEmail,
                              name: value.name,
                              city: value.address.city,
                              country: value.address.country,
                              line1: value.address.line1,
                              line2: value.address.line2 ? value.address.line2 : '',
                              postalCode: value.address.postal_code,
                              state: value.address.state,
                              phone: value.phone
                          }) 
                          dispatch(updateCustomerDetails(data.updatedCustomer))
                          setCustomerDetailsEditActive(false)
                          toast.success(t("views.subscriptions.successfullyAddedAccount"))
                      } catch (error) {
                          setLoadingBackdrop(false)
                          toast.error(t("views.subscriptions.errorWhileEditing"))
                      }
                  }
            }else{
                toast.error(t("views.subscriptions.phoneRequired"))      
            }
        }else{
          toast.error(t("views.subscriptions.emailRequired"))
        }   
    }

    const handleAddPaymentMethod = async (event) => {
        event.preventDefault();
        if(user.stripeCustomerId){
            const cardElement = elements.getElement('card')

            if (!stripe || !elements || !cardElement) {
                return;
            }

            const result = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement
            })
        
            setAddPaymentMethodActive(false)

            if (result.error) {
                toast.error(t("views.subscriptions.PMdetailsIncomplete"))
            } else {
                try {
                    const attachPMtoUserRes = await api.post('/payment/attachPMtoCustomer', {pmId: result.paymentMethod.id, customerId: user.stripeCustomerId})
                    dispatch(updateCustomerPM([{...result.paymentMethod}]))
                }catch (err) {
                    toast.error(t("views.subscriptions.attachPMerror"))
                }
            }
        }else{
            toast.error(t("views.subscriptions.noBillingDataError"))
        }
    }

    // const handleDetachPMfromCustomer = async (pmId) => {
    //     try{ 
    //         const detachRes = await api.post('/payment/detachPMfromCustomer', {pmId:pmId})
    //         setCustomerPaymentMethods([])
    //     } catch(error) {
    //         toast.error("Error while detaching payment method from your account")
    //     }
    // }

    const handleEditPaymentMethod = async (event, oldPmId) => {
        event.preventDefault()
        if(user.stripeCustomerId){
            setLoadingBackdrop(true)
            const cardElement = elements.getElement('card')

            if (!stripe || !elements || !cardElement) {
                setLoadingBackdrop(false)
                return;
            }

            const result = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement
            })
        
            setAddPaymentMethodActive(false)

            if (result.error) {
                toast.error("Payment details incomplete")
                setLoadingBackdrop(false)
            } else {
                try {
                    const detachRes = await api.post('/payment/detachPMfromCustomer', {pmId: oldPmId})
                    const attachPMtoUserRes = await api.post('/payment/attachPMtoCustomer', {pmId: result.paymentMethod.id, customerId: user.stripeCustomerId})
                    dispatch(updateCustomerPM([{...result.paymentMethod}]))
                    setLoadingBackdrop(false)
                }catch (err) {
                    toast.error(t("views.subscriptions.attachPMerror"))
                    setLoadingBackdrop(false)
                }
            }
        }else{
            toast.error(t("views.subscriptions.noBillingDataError"))
        }
    }

    const handleEditPM = () => {
        const defaultPM = customerPaymentMethods[0]
        defaultPM.openEdit = true

        // setCustomerPaymentMethods([defaultPM])
        dispatch(updateCustomerPM([defaultPM]))
    }

    const handleCloseEditPM = () => {
        const defaultPM = customerPaymentMethods[0]
        defaultPM.openEdit = false

        // setCustomerPaymentMethods([defaultPM])
        dispatch(updateCustomerPM([defaultPM]))
    }

    return (
        <Box sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center'
        }}>
            <Backdrop
            sx={(theme) => ({zIndex: theme.zIndex.drawer + 1 })}
            open={loadingBackdrop}
            onClick={() => setLoadingBackdrop(false)}
            >
            <CircularProgress/>
            </Backdrop>
            <Card variant="outlined" sx={{
                width: '100%',
                maxWidth: '600px',
                overflow: 'auto'
            }}>
                {customerDetails ? (customerDetailsEditActive ? (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        py: 1,
                        px: 2,
                        minWidth: '500px'
                    }}>
                        <Typography variant="h5" sx={{mb: 3}}>{t("views.subscriptions.enterBillingData")}</Typography>
                        <Box sx={{mb: 2}}>
                        <TextField variant="outlined" label={t("views.subscriptions.emailAddress")} fullWidth color="primary" value={customerEmail} onInput={(e) => setCustomerEmail(e.target.value)}/>
                        </Box>
                        <AddressElement options={{mode: 'billing', fields: {phone: 'always'}, validation: {phone: {required: 'always'}}, autocomplete: {mode: 'google_maps_api', apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY},
                        defaultValues: {
                            name: customerDetails.name,
                            phone: customerDetails.phone,
                            address: customerDetails.shipping.address
                        }
                    }}/>
                        <Box sx={{mt: 3, display: 'flex', gap: '10px'}}>
                            <Button onClick={handleCustomerDataEdit} variant='contained'>{t("views.subscriptions.edit")}</Button>
                            <Button variant='contained' color="error" onClick={() => setCustomerDetailsEditActive(false)}>{t("views.subscriptions.cancel")}</Button>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{px: 1, py:3, minWidth: '500px'}}>
                        <Box sx={{display: 'flex', justifyContent: "space-between", gap: '10px', alignItems: 'center', px: 2}}>
                            <Typography variant="h6">{t("views.subscriptions.billingAccountData")}</Typography>
                            <Button size="medium" endIcon={<EditIcon />} onClick={() => setCustomerDetailsEditActive(true)}>{t("views.subscriptions.edit")}</Button>
                        </Box>
                        <Grid container spacing={1}>
                            <Grid item xs={6}>
                                <List>
                                    <ListItem>
                                        <ListItemIcon>
                                            <PersonIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={customerDetails.name}
                                            secondary={t("views.subscriptions.name")}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <FlagIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={customerDetails.shipping.address.country}
                                            secondary={t("views.subscriptions.country")}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <MarkunreadMailboxIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={customerDetails.shipping.address.postal_code}
                                            secondary={t("views.subscriptions.postalCode")}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <PlaceIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={customerDetails.shipping.address.line2 ? customerDetails.shipping.address.line2 : '-'}
                                            secondary={t("views.subscriptions.addressLine2")}
                                        />
                                    </ListItem>
                                </List>
                            </Grid>
                            <Grid item xs={6}>
                                <List>
                                    <ListItem>
                                        <ListItemIcon>
                                            <EmailIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={customerDetails.email}
                                            secondary={t("views.subscriptions.emailAddress")}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <LocationCityIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={customerDetails.shipping.address.city}
                                            secondary={t("views.subscriptions.city")}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <PlaceIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={customerDetails.shipping.address.line1}
                                            secondary={t("views.subscriptions.addressLine1")}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <LocalPhoneIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={customerDetails.phone}
                                            secondary={t("views.subscriptions.phone")}
                                        />
                                    </ListItem>
                                </List>
                            </Grid>
                        </Grid>
                        <Box sx={{px: 2, display: 'flex', flexDirection: 'column', gap: '20px'}}>
                            <Typography variant="h6" sx={{mt:2}}>{t("views.subscriptions.paymentMethods")}</Typography>
                            {addPaymentMethodActive ? (
                                <form onSubmit={handleAddPaymentMethod}>
                                    <Box sx={{
                                        backgroundColor: '#252525',
                                        p: 1,
                                        borderRadius: '8px'
                                    }}>
                                        <CardElement options={{style: {
                                            base: {
                                                color: '#ffffff',
                                                backgroundColor: '#4d4d4d',
                                                lineHeight: '40px' ,
                                                padding: "10px",
                                                iconColor: "#fff"
                                            }
                                        }}}/>
                                    </Box>
                                    <button disabled={!stripe} type="submit" style={{
                                        padding: '8px',
                                        marginTop: '20px',
                                        borderRadius: '5px',
                                        border: 'none'
                                    }}>{t("views.subscriptions.submit")}</button>                
                                    <button type="button" onClick={() => setAddPaymentMethodActive(false)} style={{
                                        padding: '8px',
                                        marginTop: '20px',
                                        borderRadius: '5px',
                                        border: 'none',
                                        marginLeft: '10px'
                                    }}>{t("views.subscriptions.cancel")}</button>
                                </form>
                            ) : (
                                <List>
                                    {customerPaymentMethods ? (customerPaymentMethods.length > 0 ? (
                                        customerPaymentMethods.map(pm => {
                                            if(pm.openEdit){
                                                return (
                                                    <form onSubmit={(event) => handleEditPaymentMethod(event, pm.id)}>
                                                        <Box sx={{
                                                            backgroundColor: '#4d4d4d',
                                                            p: 1,
                                                            borderRadius: '8px'
                                                        }}>
                                                            <CardElement options={{style: {
                                                                base: {
                                                                    color: '#ffffff',
                                                                    backgroundColor: '#4d4d4d',
                                                                    lineHeight: '40px' ,
                                                                    padding: "10px",
                                                                    iconColor: "#fff"
                                                                },
                                                                
                                                            }}}/>
                                                        </Box>
                                                        <button disabled={!stripe} type="submit" style={{
                                                            padding: '8px',
                                                            marginTop: '20px',
                                                            borderRadius: '5px',
                                                            border: 'none'
                                                        }}>{t("views.subscriptions.submit")}</button>                
                                                        <button type="button" onClick={handleCloseEditPM} style={{
                                                            padding: '8px',
                                                            marginTop: '20px',
                                                            borderRadius: '5px',
                                                            border: 'none',
                                                            marginLeft: '10px'
                                                        }}>{t("views.subscriptions.cancel")}</button>
                                                    </form>
                                                )
                                            }else{
                                                return (
                                                    <ListItem key={pm.id} secondaryAction={
                                                        <IconButton edge="end" aria-label="delete payment method" onClick={handleEditPM}>
                                                            <EditIcon color="primary" />
                                                        </IconButton>
                                                    }>
                                                        <ListItemIcon>
                                                            <PaymentIcon/>
                                                        </ListItemIcon>
                                                        <ListItemText secondary="Credit card" primary={`${pm.card?.brand.charAt(0).toUpperCase()}${pm.card?.brand.slice(1)} **** ${pm.card?.last4}`}/>
                                                    </ListItem>
                                                )
                                            }
                                        })
                                    ) : (
                                        <Box sx={{display: 'flex', justifyContent: 'center'}}>
                                            <Button variant="contained" color="success" endIcon={<AddIcon/>} onClick={() => setAddPaymentMethodActive(true)}>{t("views.subscriptions.addPaymentMethod")}</Button>
                                        </Box>
                                    )) : (
                                        <ListItem>
                                            <ListItemText primary={t("views.subscriptions.noPaymentMethod")}/>  
                                        </ListItem>
                                    )}
                                </List>
                            )}
                        </Box>
                    </Box>
                )) : (
                    <Box sx={{display: 'flex', justifyContent: 'center', my: 3, width: '100%', minWidth: '500px'}}>
                        {createCustomerActive ? (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                                py: 1,
                                px: 2
                            }}>
                                <Typography variant="h5" sx={{mb: 3}}>{t("views.subscriptions.enterBillingData")}</Typography>
                                <Box sx={{mb: 2}}>
                                <TextField variant="outlined" label={t("views.subscriptions.emailAddress")} fullWidth color="primary" value={customerEmail} onInput={(e) => setCustomerEmail(e.target.value)}/>
                                </Box>
                                <AddressElement options={{mode: 'billing', fields: {phone: 'always'}, validation: {phone: {required: 'always'}}, autocomplete: {mode: 'google_maps_api', apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY}}}/>
                                <Box sx={{mt: 3, display: 'flex', gap: '10px'}}>
                                    <Button onClick={handleNewCustomerAdd} variant='contained'>{t("views.subscriptions.create")}</Button>
                                    <Button variant='contained' color="error" onClick={() => setCreateCustomerActive(false)}>{t("views.subscriptions.cancel")}</Button>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', p:2, gap: '20px'}}>
                                <Typography variant="h6" textAlign="center">{t("views.subscriptions.noBillingDataInfo")}</Typography>
                                <Button variant="contained" onClick={() => setCreateCustomerActive(true)}>
                                    {t("views.subscriptions.createBillingAccount")}
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}
            </Card>
        </Box>
    );
};

export default BillingAccount;
